if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        // Hydration gate (2026-07-19): buy-buttons renders the direct-checkout
        // button disabled so a pre-hydration click can never fire the native
        // /cart/add fallback (machine-only cart, no AS&S, buyer lands on
        // /cart). Enable it only here, now that the submit handler exists.
        // Sold-out/unavailable buttons never carry the marker and stay disabled.
        if (this.submitButton.hasAttribute('data-mtc-hydrate-enable')) {
          this.submitButton.removeAttribute('data-mtc-hydrate-enable');
          this.submitButton.removeAttribute('disabled');
        }

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      // Clear all items from cart
      async clearCart() {
        try {
          const config = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          };

          const clearResponse = await fetch('/cart/clear.js', config);
          return clearResponse.ok;
        } catch (e) {
          console.error('Error clearing cart:', e);
          return false;
        }
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const scope = this.closest('section') || document;
        const parentGroupId = `main_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;

        const selectedAddons = Array.from(scope.querySelectorAll('.js-addon-input:checked'))
          .map(el => {
            const vid = el.dataset.addonVariantId;
            return vid ? { variantId: vid, title: el.value } : null;
          })
          .filter(Boolean);

        let finalResponse = null;
        let errorHappened = false;

        try {
          // Clear cart first
          await this.clearCart();

          // ATOMIC COMPOSE (2026-07-20): machine + add-ons in ONE /cart/add
          // call. The previous sequential flow (machine alone first, then
          // each add-on) had an intermediate machine-only cart state that
          // (a) turned any later add failure into a silent machine-only
          // order (rate limit, network blip — Roy's UK repros), and (b) is
          // rejected outright by the server-side pair-validation Function,
          // which runs on cart mutations. One call: both lines land or
          // neither does; there is no invalid intermediate state.
          const machineItem = { quantity: 1, properties: { _group_id: parentGroupId } };
          for (const [key, value] of new FormData(this.form).entries()) {
            if (key === 'id') machineItem.id = Number(value);
            else if (key === 'quantity') machineItem.quantity = Number(value) || 1;
            else if (key === 'selling_plan' && value) machineItem.selling_plan = Number(value);
            else {
              const propKey = key.match(/^properties\[(.+)\]$/);
              if (propKey) machineItem.properties[propKey[1]] = value;
            }
          }

          const payload = {
            items: [
              machineItem,
              ...selectedAddons.map(addon => ({
                id: Number(addon.variantId),
                quantity: 1,
                properties: { _group_id: parentGroupId }
              }))
            ]
          };
          if (this.cart) {
            payload.sections = this.cart.getSectionsToRender().map(section => section.id);
            payload.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }

          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.body = JSON.stringify(payload);

          const resp = await fetch(`${routes.cart_add_url}`, config).then(r => r.json());
          finalResponse = resp;

          if (resp.status) {
            // Atomic: nothing was added. Surface a retryable error and stay
            // on the PDP — never enter checkout with a partial pair.
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: machineItem.id,
              errors: resp.errors || resp.description,
              message: resp.message
            });
            this.handleErrorMessage(
              resp.description || 'Something went wrong preparing your order. Please try again.'
            );

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (soldOutMessage) {
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
            }
            errorHappened = true;
          }

          if (!errorHappened) {
            // Redirect to checkout instead of cart
            window.location = window.routes.cart_url.replace('/cart', '/checkout');
            return;
          }
        } catch (e) {
          console.error(e);
        } finally {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!errorHappened) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');
          this.error = !!errorHappened;
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.checkout;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}